import React from 'react';
import { client, urlFor, generateSrcSet, urlForOptimized } from '../../lib/sanity';
import { StoryDate } from '../common/StoryDate';
import { Heart, ArrowRight, Quote, MapPin, PawPrint, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { SuccessStory } from '../../types/sanity';

export function VirtualAdoptionStories() {
  const [stories, setStories] = React.useState<SuccessStory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const [selectedStory, setSelectedStory] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchStories = async () => {
      try {
        const result = await client.fetch<SuccessStory[]>(`
          *[_type == "successStory" && adoptionType == "virtual" && isPublished == true && !(_id in path("drafts.**"))] | order(adoptionDate desc) {
            _id,
            ownerName,
            adoptionDate,
            location,
            quote,
            testimony,
            impactQuote,
            image,
            media,
            wasSpecialCase,
            "pet": pet->{
              name,
              mainCategory,
              image
            },
            "additionalPets": additionalPets[]->{
              name,
              mainCategory,
              image
            }
          }
        `);
        setStories(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching success stories:', err);
        setError('Nie udało się załadować historii adopcyjnych');
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  if (loading) {
    return (
      <div className="mt-16 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg p-8">
        <div className="text-center">
          <p className="body-large">
            Ładowanie historii...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-16 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg p-8">
        <div className="text-center">
          <p className="body-large text-accessible-red-600">
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (stories.length === 0) {
    return null;
  }

  return (
    <div className="mt-16 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black rounded-lg p-8">
      <div className="text-center mb-12">
        <h2 className="section-title mb-4">
          Historie wirtualnych adopcji
        </h2>
        <p className="body-large">
          Zobacz, jak wirtualna adopcja zmienia życie naszych podopiecznych
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {stories.map((story) => (
          <div 
            key={story._id} 
            className={`bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg overflow-hidden transform transition-all duration-300 ${
              selectedStory === story._id ? 'scale-[1.02] ring-2 ring-accessible-red-600' : 'hover:scale-[1.01]'
            } cursor-pointer`}
            onClick={() => navigate(`/historia/${story._id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/historia/${story._id}`);
              }
            }}
            onMouseEnter={() => setSelectedStory(story._id)}
            onMouseLeave={() => setSelectedStory(null)}
            role="button"
            tabIndex={0}
            title="Zobacz więcej historii adopcji wirtualnych"
          >
            <div className="relative aspect-video">
              <img
                src={story.image ? urlForOptimized(story.image, 800, 450, 80) : 
                     story.pet?.image ? urlForOptimized(story.pet.image, 800, 450, 80) :
                     '/images/animals/fundacja-znajdki-zwierzeta-012.webp'}
                srcSet={story.image ? generateSrcSet(story.image) : 
                       story.pet?.image ? generateSrcSet(story.pet.image) : 
                       ''}
                sizes="(max-width: 768px) 100vw, 50vw"
                alt={`${story.pet?.name || 'Podopieczny'} ze swoim opiekunem ${story.ownerName}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                decoding="async"
                width="800"
                height="450"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent"></div>
              {story.wasSpecialCase && (
                <div className="absolute top-4 right-4 bg-accessible-red-600 text-white px-3 py-1 rounded-full text-sm">
                  Wyjątkowy przypadek
                </div>
              )}
              <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                <PawPrint className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {story.pet?.mainCategory === 'dog' ? 'Pies' : 'Kot'}
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-5 h-5 text-accessible-red-600" />
                <span className="body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white no-margin">
                  Wirtualny opiekun: {story.ownerName}
                </span>
              </div>
              <h3 className="card-title mb-4">
                Historia {story.pet?.name}
                {story.additionalPets && story.additionalPets.length > 0 && (
                  <span className="card-subtitle text-accessible-gray-700 dark:text-white/90 high-contrast:text-white">
                    {' '}i {story.additionalPets.length} {story.additionalPets.length === 1 ? 'innego zwierzaka' : 'innych zwierzaków'}
                  </span>
                )}
              </h3>
              <Quote className="w-8 h-8 text-accessible-red-600/30 mb-4" />
              <blockquote className="quote-text mb-6">
                "{story.quote}"
              </blockquote>
              {story.impactQuote && (
                <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-4 rounded-lg mb-6">
                  <p className="body-medium italic">
                    "{story.impactQuote}"
                  </p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white no-margin">
                <StoryDate date={new Date(story.adoptionDate).toLocaleDateString('pl-PL', {
                  year: 'numeric',
                  month: 'long'
                })} />
                {story.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    <span>{story.location}</span>
                  </div>
                )}
              </div>
              {story.media && story.media.length > 0 && (
                <div className="mt-6 grid grid-cols-4 gap-2">
                  {story.media.slice(0, 4).map((media, index) => (
                    'asset' in media ? (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                        <img
                          src={urlFor(media).width(200).height(200).url()}
                          alt={`Dodatkowe zdjęcie ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {index === 3 && story.media.length > 4 && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white">
                            <span>+{story.media.length - 4}</span>
                          </div>
                        )}
                      </div>
                    ) : null
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        <button 
          onClick={() => navigate('/historie-adopcyjne')}
          className="inline-flex items-center gap-2 bg-accessible-red-600 text-white px-8 py-4 rounded-lg hover:bg-accessible-red-700 transition-colors btn-text-large"
        >
          <span>Zobacz więcej historii</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}